// #region Imports

import type { Path, Rect } from '@geom';
import {
  Compound,
  Tree,
  type Edges,
  type EID,
  type Elements,
  type NID,
  type Nodes
} from '@graph';
import type { ImageSource } from '@image';
import { IString } from '@strings';
import { assertInstanceOf, expectDefined } from '@utils';
import { newIdentifier, type Identifier } from './Identifier';
import { parseIStringLiteral } from './parseIStringLiteral';

// #endregion

/*
Conceptual Hierarchy:

- **Element** — Every element is a node or edge in the graph.
  - **Edge** — Represents a connection between two nodes, the source and target.
  - **Node** — There are two basic types of nodes, units and groups.
    - **Unit** — A basic node in the graph.
      - **Entity** — Defined in terms of its class and title. Same concept as in Flying Logic.
      - **Junctor** — Defined in terms of its logical operation (e.g., AND, OR). Same concept as in Flying Logic.
    - **Group** — A node that can contain other nodes.
*/

export class CadetDoc {
  // #region Properties

  id: Identifier;
  graph: DocGraph;
  layout: Layout;
  classes: DSLClasses;
  rawSettings?: Record<string, unknown>;
  images: Map<string, ImageSource>;
  // Declared include symbols (e.g., ['prelude']) in document order
  includes: string[];
  // Map of className -> includeSymbol that provided the class (for writer filtering)
  includeClassOrigins: Map<string, string>;

  // #endregion

  // #region Constructors

  constructor() {
    this.id = newIdentifier();
    this.graph = new Compound();
    this.layout = new Layout();
    this.classes = new DSLClasses();
    this.images = new Map();
    this.includes = [];
    this.includeClassOrigins = new Map();
  }

  // #endregion

  // #region Images API

  setImage(id: string, src: ImageSource): void {
    this.images.set(id, src);
  }

  getImage(id: string): ImageSource | undefined {
    return this.images.get(id);
  }

  hasImage(id: string): boolean {
    return this.images.has(id);
  }

  get imageIds(): string[] {
    return Array.from(this.images.keys());
  }

  // #endregion

  // #region Accessors

  hasClass(id: Identifier): boolean {
    return this.classes.hasClass(id);
  }

  isClassKindOf(id: Identifier, superCls: Identifier): boolean {
    return this.classes.isClassKindOf(id, superCls);
  }

  hasNode(node: NID): boolean {
    return this.graph.hasNode(node);
  }

  hasEdge(edge: EID): boolean {
    return this.graph.hasEdge(edge);
  }

  nodeData(node: NID): DSLNodeData {
    return this.graph.nodeData(node);
  }

  edgeData(edge: EID): DSLEdgeData {
    return this.graph.edgeData(edge);
  }

  nodeType(node: NID): NodeType {
    return this.nodeData(node).type;
  }

  isGroup(node: NID): boolean {
    return this.nodeType(node) === NodeType.GROUP;
  }

  isUnit(node: NID): boolean {
    return this.nodeType(node) === NodeType.UNIT;
  }

  groupsIn(nodes: Nodes): Nodes {
    return nodes.filter(node => this.isGroup(node));
  }

  unitsIn(nodes: Nodes): Nodes {
    return nodes.filter(node => this.isUnit(node));
  }

  nodeLayout(node: NID): DSLNodeLayout {
    return expectDefined(this.layout.node(node));
  }

  edgeLayout(edge: EID): DSLEdgeLayout {
    return expectDefined(this.layout.edge(edge));
  }

  nodeBounds(node: NID): Rect {
    return this.nodeLayout(node).bounds;
  }

  edgePath(edge: EID): Path {
    return this.edgeLayout(edge).path;
  }

  edgeBounds(edge: EID): Rect {
    return this.edgePath(edge).bounds;
  }

  nodesBounds(nodes: Nodes): Rect | undefined {
    let result: Rect | undefined;
    for (const node of nodes) {
      result = this.nodeBounds(node).union(result);
    }
    return result;
  }

  edgesBounds(edges: Edges): Rect | undefined {
    let result: Rect | undefined;
    for (const edge of edges) {
      result = this.edgeBounds(edge).union(result);
    }
    return result;
  }

  elementsBounds(elements: Elements): Rect | undefined {
    const nodesBounds = this.nodesBounds(elements.nodes);
    if (nodesBounds === undefined) {
      return undefined;
    }
    // Edges aren't always inside the bounds of the nodes,
    // as they can be routed around nodes. Thus they
    // can contribute to the overall bounds.
    const edgesBounds = this.edgesBounds(elements.edges);
    if (edgesBounds === undefined) {
      return nodesBounds;
    }
    return nodesBounds.union(edgesBounds);
  }

  nodeClass(node: NID): Identifier {
    return this.nodeData(node).cls;
  }

  setNodeClass(node: NID, cls: Identifier) {
    this.nodeData(node).cls = cls;
  }

  edgeClass(edge: EID): Identifier {
    return this.edgeData(edge).cls;
  }

  private _nodeAttrs(node: NID): ElementAttrs {
    return this.nodeData(node).attrs;
  }

  private _edgeAttrs(edge: EID): ElementAttrs {
    return this.edgeData(edge).attrs;
  }

  nodeAttr(node: NID, key: Identifier): unknown {
    return this._nodeAttrs(node)[key] ?? this.classes.getAttr(this.nodeClass(node), key);
  }

  flatNodeAttrs(node: NID): ElementAttrs {
    const result = this.classes.flatAttrs(this.nodeClass(node));
    for (const [key, value] of Object.entries(this._nodeAttrs(node))) {
      result[key] = value;
    }
    return result;
  }

  setNodeAttr(node: NID, key: Identifier, value: unknown) {
    this._nodeAttrs(node)[key] = value;
  }

  edgeAttr(edge: EID, key: Identifier): unknown {
    return this._edgeAttrs(edge)[key] ?? this.classes.getAttr(this.edgeClass(edge), key);
  }

  setEdgeAttr(edge: EID, key: Identifier, value: unknown) {
    this._edgeAttrs(edge)[key] = value;
  }

  flatEdgeAttrs(edge: EID): ElementAttrs {
    const result = this.classes.flatAttrs(this.edgeClass(edge));
    for (const [key, value] of Object.entries(this._edgeAttrs(edge))) {
      result[key] = value;
    }
    return result;
  }

  addNode(id: NID, cls: Identifier, title: IString, nodeType: NodeType = NodeType.UNIT) {
    const attrs: ElementAttrs = {};
    attrs.title = title;
    this.graph.addNode(id, { data: { cls, attrs, type: nodeType } });
  }

  /**
   * Updates the type of an existing node to the specified type.
   * This is used during type resolution to convert nodes that were
   * initially created as units but later determined to be groups.
   *
   * @param id - The identifier of the node to update
   * @param nodeType - The new node type
   */
  updateNodeType(id: NID, nodeType: NodeType): void {
    if (this.hasNode(id)) {
      this.nodeData(id).type = nodeType;
    }
  }

  setNodeTitle(node: NID, title: IString) {
    assertInstanceOf(title, IString);
    this.setNodeAttr(node, 'title', title);
  }

  nodeTitle(node: NID): IString {
    return (this.nodeAttr(node, 'title') as IString | undefined) ?? IString.EMPTY;
  }

  hasNodeTitle(node: NID): boolean {
    return !this.nodeTitle(node).isEmpty;
  }

  // Note methods
  setNodeNote(node: NID, note: IString) {
    assertInstanceOf(note, IString);
    this.setNodeAttr(node, 'note', note);
  }

  nodeNote(node: NID): IString {
    return (this.nodeAttr(node, 'note') as IString | undefined) ?? IString.EMPTY;
  }

  hasNodeNote(node: NID): boolean {
    return !this.nodeNote(node).isEmpty;
  }

  setEdgeNote(edge: EID, note: IString) {
    assertInstanceOf(note, IString);
    this.setEdgeAttr(edge, 'note', note);
  }

  edgeNote(edge: EID): IString {
    return (this.edgeAttr(edge, 'note') as IString | undefined) ?? IString.EMPTY;
  }

  hasEdgeNote(edge: EID): boolean {
    return !this.edgeNote(edge).isEmpty;
  }

  // Assistant note methods
  setNodeAssistantNote(node: NID, note: IString) {
    assertInstanceOf(note, IString);
    this.setNodeAttr(node, 'assistantNote', note);
  }

  nodeAssistantNote(node: NID): IString {
    return (this.nodeAttr(node, 'assistantNote') as IString | undefined) ?? IString.EMPTY;
  }

  hasNodeAssistantNote(node: NID): boolean {
    return !this.nodeAssistantNote(node).isEmpty;
  }

  setEdgeAssistantNote(edge: EID, note: IString) {
    assertInstanceOf(note, IString);
    this.setEdgeAttr(edge, 'assistantNote', note);
  }

  edgeAssistantNote(edge: EID): IString {
    return (this.edgeAttr(edge, 'assistantNote') as IString | undefined) ?? IString.EMPTY;
  }

  hasEdgeAssistantNote(edge: EID): boolean {
    return !this.edgeAssistantNote(edge).isEmpty;
  }

  // Bounds and Path methods (for layout directives)
  setNodeBounds(node: NID, bounds: Rect) {
    if (!this.layout.nodes.has(node)) {
      this.layout.nodes.set(node, { bounds });
    } else {
      const nodeLayout = this.layout.nodes.get(node);
      if (nodeLayout) {
        nodeLayout.bounds = bounds;
      }
    }
  }

  setEdgePath(edge: EID, path: Path) {
    if (!this.layout.edges.has(edge)) {
      this.layout.edges.set(edge, { path });
    } else {
      const edgeLayout = this.layout.edges.get(edge);
      if (edgeLayout) {
        edgeLayout.path = path;
      }
    }
  }

  // #endregion
}

// #region Types

export type ElementAttrs = Record<Identifier, unknown>;

export interface ElementClass {
  isBuiltIn: boolean;
  isAbstract: boolean;
  attrs: ElementAttrs;
}

export interface DSLElementData {
  cls: Identifier;
  attrs: ElementAttrs;
}

export interface DSLNodeLayout {
  bounds: Rect;
}

export interface DSLEdgeLayout {
  path: Path;
}

export enum NodeType {
  UNIT = 'unit',
  GROUP = 'group'
}

export interface DSLNodeData extends DSLElementData {
  type: NodeType;
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface DSLEdgeData extends DSLElementData {}

export type DocGraph = Compound<unknown, DSLNodeData, DSLEdgeData>;

// #endregion

export class Layout {
  nodes: Map<NID, DSLNodeLayout>;
  edges: Map<NID, DSLEdgeLayout>;

  constructor() {
    this.nodes = new Map<NID, DSLNodeLayout>();
    this.edges = new Map<NID, DSLEdgeLayout>();
  }

  node(node: NID): DSLNodeLayout | undefined {
    return this.nodes.get(node);
  }

  edge(edge: EID): DSLEdgeLayout | undefined {
    return this.edges.get(edge);
  }
}

export type ClassTree = Tree<unknown, ElementClass, unknown>;

export class DSLClasses {
  tree: ClassTree;

  constructor() {
    this.tree = new Tree({
      root: 'Element',
      rootData: {
        isBuiltIn: true,
        isAbstract: true,
        attrs: {
          title: IString.EMPTY
        }
      }
    });

    this.addClass('Node', 'Element', { isBuiltIn: true, isAbstract: true });
    const unitName = parseIStringLiteral(
      `'Unit'~en-US 'Unidad'~es-ES 'Einheit'~de-DE 'Unité'~fr-FR 'Единица'~ru-RU 'इकाई'~hi-IN '단위'~ko-KR 'หน่วย'~th-TH '単位'~ja-JP '单元'~zh-CN 'יחידה'~he-IL 'وحدة'~ar-SA`
    );
    this.addClass('Unit', 'Node', { isBuiltIn: true, attrs: { name: unitName } });
    this.addClass('Group', 'Node', { isBuiltIn: true });
    this.addClass('Edge', 'Element', { isBuiltIn: true });
  }

  /**
   * Adds a new class to the document with the specified superclass and attributes.
   *
   * @param id - Identifier for the new class
   * @param superCls - Identifier of the superclass this class belongs to
   * @param attrs - Attributes to be associated with the class
   */
  addClass(id: Identifier, superCls: Identifier, opts?: Partial<ElementClass>) {
    const {
      isBuiltIn = false,
      isAbstract = false,
      attrs
    } = { attrs: {}, ...(opts ?? {}) };
    this.tree.addChild(id, newIdentifier(), {
      parent: superCls,
      data: { isBuiltIn, isAbstract, attrs }
    });
  }

  /**
   * Checks if a class with the specified identifier exists in the document.
   *
   * @param id - The identifier of the class to check
   * @returns `true` if a class with the given identifier exists, `false` otherwise
   */
  hasClass(id: Identifier): boolean {
    return this.tree.hasNode(id);
  }

  /**
   * Checks if a class is a subclass of another class (or the same class).
   *
   * @param id - The identifier of the class to check
   * @param superCls - The identifier of the potential superclass
   * @returns True if the class identified by `id` is a subclass of `superCls`,
   * or if the two identifiers are the same; false otherwise
   */
  isClassKindOf(id: Identifier, superCls: Identifier): boolean {
    if (id === superCls) {
      return true;
    }
    return this.tree.hasAncestor(id, superCls);
  }

  /**
   * Sets an attribute value for a specified class, which will be inherited by
   * all elements of that class.
   *
   * @param id - The identifier of the node to modify
   * @param key - The attribute name/key to set
   * @param value - The value to assign to the attribute
   */
  setAttr(id: Identifier, key: Identifier, value: unknown) {
    this.tree.nodeData(id).attrs[key] = value;
  }

  /**
   * Retrieves an attribute value by searching up through the class hierarchy.
   * Handles nested attributes by using deep merging.
   *
   * @param id - The identifier of the node to start searching from
   * @param key - The attribute key to look for
   * @returns The attribute value if found, or undefined if the attribute
   * doesn't exist anywhere in the hierarchy path from the given node
   *
   * @remarks
   * This method implements attribute inheritance by traversing the class hierarchy
   * upward starting from the specified node. If the attribute contains nested objects,
   * it will deep merge them, with properties from more specific classes overriding
   * those from less specific classes.
   */
  getAttr(id: Identifier, key: Identifier): unknown {
    // Collection of values from class hierarchy from root to derived
    const path = this.tree.pathToRoot(id).reverse();
    let merged: unknown = undefined;

    // Helper function to deep merge objects
    const deepMerge = (target: object, source: object): object => {
      const result = { ...target };

      for (const [key, sourceValue] of Object.entries(source)) {
        // Get the target value for comparison
        const targetValue = result[key as keyof typeof result];

        // Determine if both values are objects (not arrays) for deep merging
        const isSourceObject =
          sourceValue !== null &&
          typeof sourceValue === 'object' &&
          !Array.isArray(sourceValue);

        // Check if the target value is an object (not null and not an array)
        // Use a type guard to narrow the type
        const isObjectValue = (val: unknown): val is Record<string, unknown> =>
          val !== null && typeof val === 'object' && !Array.isArray(val);

        // Check if the key exists and the target value is an object
        const keyExists = key in result;
        const isTargetObject = keyExists && isObjectValue(targetValue);

        // If both are objects, deep merge them
        if (isSourceObject && isTargetObject) {
          // Deep merge nested objects
          (result as Record<string, unknown>)[key] = deepMerge(
            targetValue as object,
            sourceValue as object
          );
        } else {
          // Override or add the property
          (result as Record<string, unknown>)[key] = sourceValue;
        }
      }

      return result;
    };

    // Go through the path from root to derived
    for (const cls of path) {
      const value = this.tree.nodeData(cls).attrs[key];

      if (value === undefined) {
        continue;
      }

      // First defined value becomes the base
      if (merged === undefined) {
        merged = value;
        continue;
      }

      // For objects (not arrays), deep merge
      if (
        typeof value === 'object' &&
        value !== null &&
        !Array.isArray(value) &&
        typeof merged === 'object' &&
        merged !== null &&
        !Array.isArray(merged)
      ) {
        merged = deepMerge(merged, value);
      } else {
        // For arrays and primitives, newer values override older ones
        merged = value;
      }
    }

    return merged;
  }

  /**
   * Retrieves the attributes for an element by traversing its class hierarchy.
   *
   * This method collects attributes from all classes in the inheritance path
   * from the root to the specified element. If the same attribute is defined
   * in multiple classes in the hierarchy, the attribute from the more specific
   * class (closer to the element in the hierarchy) will override attributes
   * from less specific classes.
   *
   * @param id - The identifier of the element to retrieve attributes for
   * @returns A map of attribute names to their values for the specified element
   */
  flatAttrs(id: Identifier): ElementAttrs {
    const path = this.tree.pathToRoot(id).reverse();
    const result: ElementAttrs = {};
    for (const cls of path) {
      const attrs = this.tree.nodeData(cls).attrs;
      for (const key in attrs) {
        result[key] = attrs[key];
      }
    }
    return result;
  }
}
